import { useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useMemo, useCallback } from "react";
import { DataTable, Plus, Switch, useToast, type ColumnDef, type ItemProps } from "@repo/ui";
import { cn } from "@repo/utils";
import { useTasks } from "~/apis/use-tasks";
import BigButton from "~/components/ui/big-button";
import { PageContentLayout } from "~/components/ui/page-content-layout";
import { PageHeader } from "~/components/ui/page-header";
import { PageLayout } from "~/components/ui/page-layout";
import { useConfigurationSync } from "~/hooks/use-sync";
import { workflowStatusMap } from "~/pages/administration/workflows-page/workflows-constants";
import type { Workflow, WorkflowState } from "~/types/api";
import type { DropdownActionContext } from "~/types/common";
import { workflowBundleQueryOptions } from "./hooks/apis/use-fetch-workflow-details";
import { useUpgradeWorkflow } from "./hooks/apis/use-upgrade-workflow";
import { useWorkflowsData } from "./hooks/apis/use-workflows-data";
import { useWorkflowEditDialogs } from "./hooks/use-workflow-edit-dialogs";
import { useWorkflowFieldDialog } from "./hooks/use-workflow-field-dialog";
import { useWorkflowsDialog } from "./workflows-dialog/use-workflows-dialog";

export type WorkflowData = Workflow & {
  customerName: string;
  subRows?: WorkflowData[];
};

export type WorkflowActionsContext = DropdownActionContext<WorkflowData>;

const columns: ColumnDef<WorkflowData>[] = [
  {
    key: "id",
    header: "ID",
    width: "w-20",
    sortable: true,
    searchable: true,
  },
  {
    key: "name",
    header: "Package Name",
    width: "flex-1",
    sortable: true,
    searchable: true,
  },
  {
    key: "customerName",
    header: "Customer",
    width: "flex-1",
    sortable: true,
    searchable: true,
    filterable: true,
  },
  {
    key: "state",
    header: "Status",
    width: "flex-1",
    sortable: true,
    filterable: true,
    render: (value) => (
      <span
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium",
          value === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
        )}
      >
        {workflowStatusMap[value as WorkflowState]}
      </span>
    ),
  },
  {
    key: "version",
    header: "Version",
    width: "flex-1",
    sortable: true,
    searchable: true,
    filterable: true,
    render: (value) => (
      <span>
        {value !== undefined && value !== null ? (
          `v. ${String(value).padStart(2, "0")}`
        ) : (
          <span className="text-sm text-gray-500">No previous version</span>
        )}
      </span>
    ),
  },
];

export const WorkflowsPage = () => {
  useConfigurationSync("Workflow");
  useConfigurationSync("Status");
  useConfigurationSync("Field");
  useConfigurationSync("List");

  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { tasks } = useTasks();
  const [showAll, setShowAll] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { workflows, isLoading: isDataLoading, isError: isDataError } = useWorkflowsData(showAll);
  const { upgradeWorkflow, isUpgrading } = useUpgradeWorkflow();
  const { showWorkflowDialog } = useWorkflowsDialog();
  const { showRenameDialog, showStatusDialog } = useWorkflowFieldDialog();
  const { showEditMetadataDialog, showEditStatusesDialog, showEditActionsDialog } =
    useWorkflowEditDialogs();
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const handleSwitchChange = (checked: boolean) => {
    if (isDisabled) return;

    setShowAll(checked);
    setIsDisabled(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsDisabled(false);
    }, 1500);
  };

  const handleUpgradeWorkflow = useCallback(
    async (workflow: WorkflowData) => {
      setIsFetchingDetails(true);
      try {
        const workflowDetails = await queryClient.fetchQuery<Workflow>(
          workflowBundleQueryOptions(workflow.id)
        );
        if (!workflowDetails.previousVersionId) {
          throw new Error("Workflow ID is missing");
        }
        upgradeWorkflow(workflowDetails.previousVersionId);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch workflow details";
        showToast("error", errorMessage, "Failed to fetch workflow details");
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [queryClient, showToast, upgradeWorkflow]
  );

  const handleCreateCopy = useCallback(
    async (workflow: WorkflowData) => {
      setIsFetchingDetails(true);
      try {
        const workflowDetails = await queryClient.fetchQuery<Workflow>(
          workflowBundleQueryOptions(workflow.id)
        );
        showWorkflowDialog({
          sourceWorkflow: workflowDetails,
          title: `Create New Workflow Based on ${workflowDetails.name}`,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch workflow details";
        showToast("error", errorMessage, "Failed to fetch workflow details");
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [queryClient, showToast, showWorkflowDialog]
  );

  const handleEdit = useCallback(
    async (workflow: WorkflowData) => {
      setIsFetchingDetails(true);
      try {
        const workflowDetails = await queryClient.fetchQuery<Workflow>(
          workflowBundleQueryOptions(workflow.id)
        );
        showWorkflowDialog({
          sourceWorkflow: workflowDetails,
          title: "Edit Workflow Package - " + workflowDetails.name,
          isUpgrade: true,
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch workflow details";
        showToast("error", errorMessage, "Failed to fetch workflow details");
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [queryClient, showToast, showWorkflowDialog]
  );

  const actions: ItemProps<WorkflowActionsContext>[] = useMemo(
    () => [
      {
        id: "change-name",
        label: "Change Workflow Package Name",
        onClick: (ctx: WorkflowActionsContext) => showRenameDialog(ctx.row),
      },
      {
        id: "change-status",
        label: "Change Workflow Package Status",
        onClick: (ctx: WorkflowActionsContext) =>
          showStatusDialog(
            ctx.row,
            workflows || [],
            tasks.find((t) => t.workflowId === ctx.row.id)?.active || false
          ),
      },
      {
        id: "edit-metadata",
        label: "Edit Metadata Fields",
        show: (ctx: WorkflowActionsContext) => ctx.row.state === "WorkInProgress",
        onClick: (ctx: WorkflowActionsContext) => showEditMetadataDialog(ctx.row),
      },
      {
        id: "edit-statuses",
        label: "Edit Item Statuses",
        show: (ctx: WorkflowActionsContext) => ctx.row.state === "WorkInProgress",
        onClick: (ctx: WorkflowActionsContext) => showEditStatusesDialog(ctx.row),
      },
      {
        id: "edit-actions",
        label: "Edit Item Actions",
        show: (ctx: WorkflowActionsContext) => ctx.row.state === "WorkInProgress",
        onClick: (ctx: WorkflowActionsContext) => showEditActionsDialog(ctx.row),
      },
      {
        id: "create-copy",
        label: "Copy Workflow Package",
        onClick: (ctx: WorkflowActionsContext) => handleCreateCopy(ctx.row),
        show: (ctx: WorkflowActionsContext) => ctx.row.state === "Active",
        disabled: isUpgrading || isFetchingDetails,
      },
      {
        id: "upgrade",
        label: "Upgrade Workflow Package",
        onClick: (ctx: WorkflowActionsContext) => handleUpgradeWorkflow(ctx.row),
        show: (ctx: WorkflowActionsContext) => {
          const parentIsActive = workflows?.some(
            (w) => w.id === ctx.row.previousVersionId && w.state === "Active"
          );
          return ctx.row.state === "WorkInProgress" && (parentIsActive ?? false);
        },
        disabled: isUpgrading || isFetchingDetails,
      },
      {
        id: "edit",
        label: "Edit Workflow Package",
        onClick: (ctx: WorkflowActionsContext) => handleEdit(ctx.row),
        show: (ctx: WorkflowActionsContext) => {
          const hasWip = workflows?.some(
            (w) => w.previousVersionId === ctx.row.id && w.state === "WorkInProgress"
          );
          return ctx.row.state === "Active" && !hasWip;
        },
        disabled: isUpgrading || isFetchingDetails,
      },
    ],
    [
      isUpgrading,
      isFetchingDetails,
      showRenameDialog,
      showStatusDialog,
      workflows,
      tasks,
      showEditMetadataDialog,
      showEditStatusesDialog,
      showEditActionsDialog,
      handleCreateCopy,
      handleUpgradeWorkflow,
      handleEdit,
    ]
  );

  const groupedWorkflows = useMemo(() => {
    if (!workflows) return [];

    const workflowItems: WorkflowData[] = workflows.map((w) => ({
      ...w,
      subRows: [],
    }));

    const workflowMap = new Map<number, WorkflowData>();
    workflowItems.forEach((w) => workflowMap.set(w.id, w));

    const topLevelWorkflows: WorkflowData[] = [];

    workflowItems.forEach((w) => {
      if (w.state === "WorkInProgress" && w.previousVersionId !== null) {
        const parent = workflowMap.get(w.previousVersionId);
        if (parent) {
          parent.subRows!.push(w);
          return;
        }
      }
      topLevelWorkflows.push(w);
    });

    const cleanWorkflows = (items: WorkflowData[]) => {
      items.forEach((item) => {
        if (item.subRows && item.subRows.length === 0) {
          delete item.subRows;
        } else if (item.subRows) {
          cleanWorkflows(item.subRows);
        }
      });
    };

    cleanWorkflows(topLevelWorkflows);

    return topLevelWorkflows;
  }, [workflows]);

  return (
    <PageLayout>
      <PageHeader
        title="Workflows"
        controls={
          <BigButton onClick={() => showWorkflowDialog()} icon={<Plus />} iconPosition="right">
            <span className="max-lg:hidden">Create</span>
          </BigButton>
        }
      />
      <PageContentLayout>
        <DataTable<WorkflowData, WorkflowActionsContext>
          isLoading={isDataLoading}
          isError={isDataError}
          data={groupedWorkflows || []}
          columns={columns}
          actions={actions}
          toolbarActions={
            <div className="flex items-center gap-4">
              <label className="text-gxe-primary text-sm font-bold" htmlFor="show-all-switch">
                Show all
              </label>
              <Switch
                id="show-all-switch"
                checked={showAll}
                onCheckedChange={handleSwitchChange}
                disabled={isDisabled}
                className="data-[state=checked]:bg-gxe-primary bg-gray-300"
              />
            </div>
          }
        />
      </PageContentLayout>
    </PageLayout>
  );
};
